/* eslint-disable @typescript-eslint/no-explicit-any */

import type { ButtonHTMLAttributes, ComponentClass, DetailedHTMLFactory, MouseEvent } from 'react';
import React, { createRef, PureComponent } from 'react';

import type { Defaultize } from '../lib';
import { compose, composeU } from '../lib/compose';
import { Keys } from '../lib/keyboard';
import { mergeRefs } from '../lib/mergeRefs';
import { Palettes } from '../PaletteGeneration';
import { Spin } from '../Spin';
import { withBaseline } from './_baseline/Button_baseline';
import { withSizeL } from './_size/Button_size_l';
import { withSizeS } from './_size/Button_size_s';
import { withTypeLink } from './_type/Button_type_link';
import { withTypeReset } from './_type/Button_type_reset';
import { withTypeSubmit } from './_type/Button_type_submit';
import { withViewBrand } from './_view/Button_view_brand';
import { withViewClear } from './_view/Button_view_clear';
import { withViewContrast } from './_view/Button_view_contrast';
import { withViewFloat } from './_view/Button_view_float';
import { withViewGhost } from './_view/Button_view_ghost';
import { withViewMedia } from './_view/Button_view_media';
import { withViewPrimary } from './_view/Button_view_primary';
import { withViewSecondary } from './_view/Button_view_secondary';
import { withWidthMax } from './_width/Button_width_max';
import { cnButton } from './Button.cn';
import type { ButtonBaseProps, ButtonElement, ButtonProps } from './Button.types';
import { ButtonIcon } from './ButtonIcon';
import { ButtonText as Text } from './Text/Button-Text';
import { getIsSpinThemeInverted } from './utils/getIsSpinThemeInverted';

import './Button.css';

// https://st.yandex-team.ru/ISL-1667
type ButtonEnhancedAttributes = { autoComplete?: string | null };
type ReactButtonElement = DetailedHTMLFactory<
    ButtonHTMLAttributes<ButtonElement> & ButtonEnhancedAttributes,
    ButtonElement
>;

const defaultProps = {
    autoComplete: 'off',
    pressKeys: [Keys.SPACE, Keys.ENTER] as string[],
    prvntKeys: [] as string[],
    as: 'button',
    type: 'button',
} as const;

type DefaultProps = keyof typeof defaultProps;

type ButtonInternalProps = {
    theme: any;
    baseline: any;
    pin: any;
    width: any;
};

const enhance = compose(
    composeU(
        withViewBrand,
        withViewPrimary,
        withViewSecondary,
        withViewGhost,
        withViewFloat,
        withViewClear,
        withViewContrast,
        withViewMedia,
    ),
    composeU(withTypeLink, withTypeSubmit, withTypeReset),
    composeU(withSizeL, withSizeS),
    composeU(withBaseline, withWidthMax),
);

/**
 * @deprecated Use import { Button } from '@yandex-int/hr-components/HrButton'
 */
export const Button = enhance(
    class extends PureComponent<Defaultize<ButtonBaseProps, DefaultProps>> {
        static displayName = cnButton();

        static defaultProps = defaultProps;

        /**
         * Ссылка на верхний DOM узел.
         */
        private readonly internalInnerRef = createRef<ButtonElement>();

        componentDidMount() {
            if (process.env.NODE_ENV === 'development')
                console.warn('[Button]: Эта версия компонента устарела и скоро будет удалена');
        }

        render() {
            const {
                addonAfter,
                addonBefore,
                autoComplete,
                checked,
                children,
                icon,
                iconLeft,
                iconRight,
                progress,
                disabled,
                as: AsComponent,
                type = 'button',
                className,
                innerRef,
                controlRef,
                title,
                role,
                pressKeys: _pressKeys,
                prvntKeys: _prvntKeys,
                // Извлекаем свойства, т.к. они не нужны на DOM узле
                // FIXME: https://github.com/bem/bem-react/issues/381
                view = 'outline',
                size = 'm',
                baseline: _baseline,
                pin: _pin,
                width: _width,
                disableFocusOnClick: _disableFocusOnClick,
                inline,
                ...buttonProps
            } = this.props as ButtonProps & ButtonInternalProps;
            const Component = AsComponent as ReactButtonElement;
            const refKey = typeof AsComponent === 'string' ? 'ref' : 'innerRef';
            (buttonProps as any)[refKey] = mergeRefs(this.internalInnerRef, innerRef, controlRef);

            const showChildren = (Boolean(children) && children !== true) || children === 0;

            const isOnlyIcon = !showChildren && Boolean(icon);

            return (
                <Component
                    {...buttonProps}
                    type={type as any}
                    disabled={disabled}
                    className={cnButton(
                        {
                            checked,
                            progress,
                            view: inline ? 'ghost' : view,
                            size: inline ? undefined : size,
                            inline,
                            onlyIcon: isOnlyIcon,
                        },
                        [className],
                    )}
                    onClick={this.onClick}
                    onMouseDown={this.onMouseDown}
                    onMouseUp={this.onMouseUp}
                    aria-pressed={checked}
                    aria-disabled={disabled || progress}
                    // https://st.yandex-team.ru/ISL-1667
                    autoComplete={autoComplete}
                    title={title}
                    role={role}
                    aria-busy={progress}
                >
                    {addonBefore}
                    <span className={cnButton('HoverFade')} />
                    <ButtonIcon icon={icon} iconLeft={iconLeft} size={size} />
                    {progress && (
                        <Spin
                            className={cnButton('Spin', [
                                getIsSpinThemeInverted({ view, checked })
                                    ? Palettes.inverse
                                    : undefined,
                            ])}
                            progress
                            position="center"
                        />
                    )}
                    {showChildren && <Text>{children}</Text>}
                    <ButtonIcon iconRight={iconRight} size={size} />
                    {addonAfter}
                </Component>
            );
        }

        protected onClick = (event: MouseEvent<ButtonElement>) => {
            if (this.internalInnerRef.current !== null && !this.props.disableFocusOnClick) {
                // Выставляем программно focus, т.к. в браузерах Safari и Firefox
                // клик по кнопке не выставляет фокус автоматически.
                this.internalInnerRef.current.focus();
            }

            if (this.props.onClick !== undefined && !this.props.progress) {
                this.props.onClick(event);
            }
        };

        protected onMouseDown = (event: MouseEvent<ButtonElement>) => {
            if (this.props.disableFocusOnClick) {
                event.preventDefault();
            }
            this.props.onMouseDown?.(event);
        };
        protected onMouseUp = (event: MouseEvent<ButtonElement>) => {
            if (this.props.disableFocusOnClick) {
                event.preventDefault();
            }
            this.props.onMouseUp?.(event);
        };
    } as ComponentClass<ButtonBaseProps>,
);
